import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";

export default function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);

  return (
    <section
      data-testid="hero-section"
      className="relative h-[100vh] min-h-[640px] w-full overflow-hidden bg-stone-900"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1601956370514-67f15b1dd95f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHxjaW5xdWUlMjB0ZXJyZSUyMGNsaWZmcyUyMGNvbG9yZnVsJTIwaG91c2VzfGVufDB8fHx8MTc3Njk0MjczMnww&ixlib=rb-4.1.0&q=85"
          alt="Vernazza at sunset, Cinque Terre"
          className="w-full h-full object-cover kenburns"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/70" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-20 lg:pb-28"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-white/80 text-[11px] sm:text-xs tracking-[0.3em] uppercase font-semibold flex items-center gap-3"
        >
          <span className="h-px w-10 bg-white/60 inline-block" />
          Dispatch No. 47 — Liguria, Italy
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.35 }}
          className="font-serif text-white text-5xl sm:text-7xl lg:text-[104px] font-light leading-[0.95] tracking-tight mt-6 max-w-5xl"
        >
          The five villages
          <br />
          <em className="italic font-light text-white/95">on the cliff.</em>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-8 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10"
        >
          <p className="text-white/85 max-w-xl text-[15px] sm:text-base leading-relaxed">
            A slow-travel journal of trofie pasta, terraced vineyards, ferry rides at dawn, and the kind of people who terrace hillsides to grow grapes for wine.
          </p>
          <Link
            to="/italy/cinque-terre"
            data-testid="hero-read-story"
            className="group inline-flex items-center gap-3 text-white text-[13px] tracking-[0.2em] uppercase font-semibold border-b border-white/70 pb-2 hover:border-white transition-all w-fit"
          >
            Read the story
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="absolute bottom-8 right-6 lg:right-10 flex items-center gap-2 text-white/70 text-xs tracking-widest"
        >
          <MapPin size={14} />
          44.1062°N · 9.7243°E
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 1 }}
        className="absolute bottom-6 left-6 lg:left-10 z-10 flex items-center gap-3 text-white/70"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <span className="relative block w-10 h-px bg-white/40 overflow-hidden">
          <span className="absolute inset-0 bg-white animate-[slide_2s_ease-in-out_infinite]" style={{ transformOrigin: "left" }} />
        </span>
      </motion.div>

      <style>{`
        @keyframes slide { 0%{transform:scaleX(0)} 50%{transform:scaleX(1)} 100%{transform:scaleX(0);transform-origin:right} }
      `}</style>
    </section>
  );
}
